import styled, {createGlobalStyle, keyframes} from "styled-components";
import BaseButton from "components/BaseButton";
import {pillShape} from "components/BaseButton.styles";
import {brand, fontFamilies, radii} from "theme/tokens";

/** daisyUI 5.7.37's vendored holo aura; the pastel stops belong to the library effect. */
const auraSpin = keyframes`
	to { --aura-angle: 360deg; transform: translateZ(1px); }
`;

export const AuraAngleProperty = createGlobalStyle`
	@property --aura-angle {
		syntax: "<angle>";
		inherits: false;
		initial-value: 0deg;
	}
`;

export const AuraWrap = styled.span`
	--aura-padding: 0.125rem;
	display: inline-flex;
	max-width: calc(100% - 10px);
	min-width: 0;
	position: relative;
	align-self: center;
	margin-block: 2px;
	padding: var(--aura-padding);
	border-radius: 999px;
	/* Four pastel repeats keep the small ring quieter than the library's large demo. */
	background: repeating-conic-gradient(from var(--aura-angle),
		oklch(82% 0.17 327), oklch(75% 0.12 274), oklch(82% 0.11 191),
		oklch(91% 0.11 105), oklch(88% 0.08 68), oklch(82% 0.17 327) 25%);
	animation: ${auraSpin} 16s linear infinite;

	&::before, &::after {
		content: "";
		position: absolute;
		top: 50%;
		left: 50%;
		translate: -50% -50%;
		width: 100%;
		height: 100%;
		display: block;
		z-index: 0;
		border-radius: inherit;
		background: inherit;
		animation: inherit;
		opacity: 0.5;
		filter: blur(0.2rem);
		pointer-events: none;
	}
	&::after { opacity: 0.16; filter: blur(0.55rem); }
	& > * { position: relative; z-index: 1; }
	@media (prefers-reduced-motion: reduce) {
		animation: none;
		&::before, &::after { animation: none; }
	}
`;

/** A 34px visible pill with a 44px target, including on touch devices. */
export const PersonaliseEntry = styled(BaseButton)`
	${pillShape};
	/* As with pillShape, outrank the variant regardless of stylesheet insertion order. */
	&& {
		min-height: 34px;
		padding: 6px 16px;
		border: 0;
		font-size: 13px;
		font-weight: 600;
		display: inline-flex;
		align-items: center;
		gap: 6px;
		min-width: 0;
		max-width: 100%;
		white-space: normal;
		text-align: center;
		line-height: 1.3;
		background: ${({theme}) => theme.tokens.card};
		color: ${({theme}) => theme.tokens.ink};
	}
	& svg { flex: none; }
	/* Centred like BaseButton's compact control; margins reserve its invisible extension. */
	&::after {
		content: "";
		position: absolute;
		left: 0;
		right: 0;
		top: 50%;
		height: max(100%, 44px);
		transform: translateY(-50%);
		border-radius: inherit;
	}
	&:hover:not(:disabled) {
		background: ${({theme}) => theme.tokens.field};
		color: ${({theme}) => theme.tokens.ink};
	}
`;

/**
 * The panel is the container the row's layout reads (kitecorp/transferify-ui#3320, P13): a narrow card shows the
 * example page beside the heading and button stacked; from {@link WIDE_OFFER_PX} of panel width the
 * standard page and both captions return, and the button moves beside the words.
 */
const WIDE_OFFER_PX = 480;

export const OfferPanel = styled.section`
	container-type: inline-size;
	padding: 12px 14px;
	border-radius: ${radii.panel};
	background: ${({theme}) => theme.tokens.card2};
`;

export const OfferRow = styled.div`
	display: grid;
	grid-template-columns: auto minmax(0, 1fr);
	align-items: center;
	gap: 14px;
	@container (min-width: ${WIDE_OFFER_PX}px) { gap: 18px; }
`;

export const OfferHeading = styled.h3`
	margin: 0;
	font-size: 11px;
	line-height: 1.4;
	font-weight: 700;
	letter-spacing: 0.09em;
	text-transform: uppercase;
	color: ${({theme}) => theme.tokens.ink2};
	text-wrap: balance;
`;

export const OfferDetails = styled.div`
	display: grid;
	justify-items: start;
	gap: 6px;
	min-width: 0;
	@container (min-width: ${WIDE_OFFER_PX}px) {
		grid-template-columns: minmax(0, 1fr) auto;
		align-items: center;
		column-gap: 16px;
		& > :not(:last-child) { grid-column: 1; }
		& > :last-child { grid-column: 2; grid-row: 1 / span 2; }
	}
`;

export const PreviewPair = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 8px;
`;

/** `$standard` is the plain page, shown only beside a wide panel; every caption is too. */
export const PagePreview = styled.figure<{$standard?: boolean}>`
	margin: 0;
	display: ${({$standard}) => $standard ? "none" : "grid"};
	justify-items: center;
	gap: 3px;
	min-width: 0;
	& figcaption {
		display: none;
		font-size: 11px;
		line-height: 1.4;
		color: ${({theme}) => theme.tokens.ink2};
		text-align: center;
	}
	@container (min-width: ${WIDE_OFFER_PX}px) {
		display: grid;
		& figcaption { display: block; }
	}
`;

export const PreviewBand = styled.div`
	position: absolute;
	inset: 0 0 auto;
	height: 13px;
	background: var(--example-accent);
`;

export const PreviewAvatar = styled.div`
	position: relative;
	width: 11px;
	height: 11px;
	border-radius: 50%;
	background: var(--example-accent, ${({theme}) => theme.tokens.blue});
	opacity: 0.85;
`;

export const PreviewTitle = styled.div`
	min-height: 8px;
	font: italic 8px/1 ${fontFamilies.displaySerif};
	letter-spacing: 0.04em;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const PreviewLine = styled.div`
	width: 30px;
	height: 4px;
	border-radius: 2px;
	background: ${({theme}) => theme.tokens.line};
`;

export const PreviewFile = styled.div`
	width: 100%;
	height: 7px;
	border-radius: 3px;
	background: ${({theme}) => theme.tokens.field};
	border: 1px solid ${({theme}) => theme.tokens.line};
`;

export const PreviewButton = styled.div`
	width: 100%;
	height: 9px;
	margin-top: 1px;
	border-radius: 5px;
	background: var(--example-accent, ${({theme}) => theme.tokens.blue});
`;

export const PreviewCard = styled.div<{$example?: boolean}>`
	/* Deliberate example brand, not the current sender's styling. Reuses the existing red token. */
	--example-accent: ${({$example}) => $example ? brand.error : undefined};
	position: relative;
	overflow: clip;
	width: 60px;
	box-sizing: border-box;
	border-radius: 9px;
	background: ${({theme}) => theme.tokens.card};
	border: 1px solid ${({theme}) => theme.tokens.line};
	padding: 15px 6px 6px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 3px;
	box-shadow: 0 4px 10px ${({theme}) => `color-mix(in srgb, ${theme.tokens.ink} 6%, transparent)`};
	${PreviewAvatar} { box-shadow: ${({$example, theme}) => $example ? `0 0 0 1.5px ${theme.tokens.card}` : "none"}; }
`;

export const Status = styled.p`
	position: relative;
	z-index: 6;
	margin: 0;
	color: ${({theme}) => theme.tokens.ink3};
	font-size: 13px;
	line-height: 1.5;
`;
